'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import NavBar from '@/components/NavBar';
import AdSenseUnit from '@/components/AdSenseUnit';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  cover_image?: string;
  cover_image_alt?: string;
  category?: string;
  categories?: string[];
  tags?: string[];
  author?: string;
  is_featured?: boolean;
  views?: number;
  created_at?: string;
}

const PAGE_SIZE = 12;

const CATEGORY_COLORS: Record<string, string> = {
  News: 'bg-red-600',
  Sports: 'bg-blue-600',
  Entertainment: 'bg-purple-600',
  Music: 'bg-pink-600',
  Culture: 'bg-orange-500',
  Lifestyle: 'bg-teal-600',
  Technology: 'bg-cyan-600',
  Business: 'bg-yellow-600',
  Politics: 'bg-gray-600',
  Opinion: 'bg-indigo-600',
  Interview: 'bg-green-600',
  Events: 'bg-rose-600',
};

const ALL_CATEGORIES = [
  'All', 'News', 'Sports', 'Entertainment', 'Music', 'Culture',
  'Lifestyle', 'Technology', 'Business', 'Politics', 'Opinion', 'Interview', 'Events',
];

function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function estimateReadTime(content?: string, excerpt?: string): string {
  const text = content || excerpt || '';
  const words = text.split(/\s+/).filter(Boolean).length;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} min read`;
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeTag, setActiveTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'newest' | 'popular'>('newest');
  const supabase = createClient();

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('news_posts')
          .select('id, title, slug, excerpt, content, cover_image, cover_image_alt, category, categories, tags, author, is_featured, views, created_at')
          .eq('is_published', true)
          .or('scheduled_at.is.null,scheduled_at.lte.' + new Date().toISOString())
          .order('created_at', { ascending: false });
        if (error) throw error;
        setPosts(data || []);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, activeTag, searchQuery, sortBy]);

  // Collect all unique tags from posts
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    posts.forEach(p => {
      if (Array.isArray(p.tags)) p.tags.forEach(t => t && tagSet.add(t));
    });
    return Array.from(tagSet).slice(0, 20);
  }, [posts]);

  // Available categories (only those with posts)
  const availableCategories = useMemo(() => {
    const used = new Set(posts.map(p => p.category || 'News'));
    return ALL_CATEGORIES.filter(c => c === 'All' || used.has(c));
  }, [posts]);

  const filteredPosts = useMemo(() => {
    let result = posts.filter(p => {
      const postCat = p.category || 'News';
      const matchesCategory = activeCategory === 'All' || postCat === activeCategory;
      const matchesTag = !activeTag || (Array.isArray(p.tags) && p.tags.includes(activeTag));
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        p.title.toLowerCase().includes(q) ||
        (p.excerpt || '').toLowerCase().includes(q) ||
        (p.author || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (Array.isArray(p.tags) && p.tags.some(t => t.toLowerCase().includes(q)));
      return matchesCategory && matchesTag && matchesSearch;
    });

    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => (b.views || 0) - (a.views || 0));
    }

    return result;
  }, [posts, activeCategory, activeTag, searchQuery, sortBy]);

  const featured = useMemo(() => posts.find(p => p.is_featured) || posts[0], [posts]);

  const gridPosts = useMemo(() => {
    const showingAll = activeCategory === 'All' && !searchQuery && !activeTag;
    return showingAll && featured ? filteredPosts.filter(p => p.id !== featured.id) : filteredPosts;
  }, [filteredPosts, featured, activeCategory, searchQuery, activeTag]);

  const totalPages = Math.ceil(gridPosts.length / PAGE_SIZE);
  const paginatedPosts = gridPosts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const showFeaturedHero = activeCategory === 'All' && !searchQuery && !activeTag && !!featured;

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setActiveCategory('All');
    setActiveTag('');
  }, []);

  const hasActiveFilters = searchQuery || activeCategory !== 'All' || activeTag;

  return (
    <div className="min-h-screen bg-surface-primary font-condensed">
      <NavBar activePage="other" hasTicker={false} />

      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-stroke-primary pb-5">
            <div>
              <div className="flex items-baseline gap-3 mb-1">
                <h1 className="text-text-primary text-4xl md:text-5xl font-normal tracking-tight">VIDDSTAR</h1>
                <span className="text-brand-red font-bold text-4xl md:text-5xl">BLOG</span>
              </div>
              <p className="text-text-muted text-sm">
                {loading ? 'Loading articles…' : `${posts.length} article${posts.length !== 1 ? 's' : ''} published`}
              </p>
            </div>

            {/* Search */}
            <div className="relative w-full sm:w-80">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search articles, topics, authors…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-surface-secondary border border-stroke-primary text-text-primary text-sm placeholder-text-muted pl-9 pr-9 py-2.5 rounded-[4px] focus:outline-none focus:border-brand-red transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          /* Skeleton */
          <div className="space-y-8">
            <div className="animate-pulse bg-surface-secondary border border-stroke-primary h-72 rounded-[4px]" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse space-y-3">
                  <div className="bg-surface-tertiary w-full rounded-[4px]" style={{ aspectRatio: '16/9' }} />
                  <div className="h-4 bg-surface-tertiary rounded w-1/3" />
                  <div className="h-5 bg-surface-tertiary rounded w-3/4" />
                  <div className="h-4 bg-surface-tertiary rounded w-full" />
                </div>
              ))}
            </div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-32">
            <svg className="w-16 h-16 text-text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <p className="text-text-secondary text-xl mb-2">No blog posts yet</p>
            <p className="text-text-muted text-sm">Check back soon for the latest articles.</p>
          </div>
        ) : (
          <>
            {/* Featured Hero */}
            {showFeaturedHero && (
              <Link
                href={`/news/${featured!.slug}`}
                className="group mb-8 border border-stroke-primary hover:border-brand-red transition-colors duration-200 block overflow-hidden"
              >
                <div className="grid grid-cols-1 lg:grid-cols-5">
                  <div className="lg:col-span-3 relative overflow-hidden bg-surface-tertiary" style={{ minHeight: '300px' }}>
                    {featured!.cover_image ? (
                      <img
                        src={featured!.cover_image}
                        alt={featured!.cover_image_alt || featured!.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 absolute inset-0"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-surface-tertiary">
                        <svg className="w-16 h-16 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="bg-brand-red text-white text-xs font-bold uppercase px-2 py-1">Featured</span>
                      {featured!.category && (
                        <span className={`text-white text-xs font-bold uppercase px-2 py-1 ${CATEGORY_COLORS[featured!.category] || 'bg-surface-secondary'}`}>
                          {featured!.category}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="lg:col-span-2 p-6 md:p-8 flex flex-col justify-center bg-surface-secondary">
                    <div className="flex items-center gap-2 mb-3 text-xs text-text-muted">
                      <span>{formatDate(featured!.created_at)}</span>
                      <span>·</span>
                      <span>{estimateReadTime(featured!.content, featured!.excerpt)}</span>
                      {featured!.views !== undefined && (
                        <>
                          <span>·</span>
                          <span>{featured!.views.toLocaleString()} views</span>
                        </>
                      )}
                    </div>
                    <h2 className="text-text-primary text-2xl md:text-3xl font-bold leading-tight mb-3 group-hover:text-brand-red transition-colors duration-150">
                      {featured!.title}
                    </h2>
                    {featured!.excerpt && (
                      <p className="text-text-secondary text-sm leading-relaxed mb-4 line-clamp-4">{featured!.excerpt}</p>
                    )}
                    {Array.isArray(featured!.tags) && featured!.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {featured!.tags.slice(0, 4).map(tag => (
                          <span key={tag} className="text-xs px-2 py-0.5 bg-surface-tertiary text-text-muted border border-stroke-primary rounded-[2px]">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-stroke-primary">
                      <span className="text-text-muted text-xs">By {featured!.author || 'ViddStar'}</span>
                      <span className="inline-flex items-center gap-1.5 text-brand-red text-xs font-bold uppercase tracking-wide group-hover:gap-2.5 transition-all duration-150">
                        Read Article
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Filters Row */}
            <div className="mb-6 space-y-3">
              {/* Category Filters + Sort */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 overflow-x-auto pb-1 flex-1">
                  {availableCategories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`flex-shrink-0 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-[4px] border transition-all duration-150 ${
                        activeCategory === cat
                          ? 'bg-brand-red border-brand-red text-white' :'bg-transparent border-stroke-primary text-text-secondary hover:border-stroke-light hover:text-text-primary'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {/* Sort */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-text-muted text-xs uppercase tracking-wide hidden sm:inline">Sort:</span>
                  <button
                    onClick={() => setSortBy('newest')}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-[4px] border transition-all duration-150 ${
                      sortBy === 'newest' ?'bg-surface-tertiary border-stroke-light text-text-primary' :'bg-transparent border-stroke-primary text-text-secondary hover:border-stroke-light hover:text-text-primary'
                    }`}
                  >
                    Newest
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-[4px] border transition-all duration-150 ${
                      sortBy === 'popular' ?'bg-surface-tertiary border-stroke-light text-text-primary' :'bg-transparent border-stroke-primary text-text-secondary hover:border-stroke-light hover:text-text-primary'
                    }`}
                  >
                    Popular
                  </button>
                </div>
              </div>

              {/* Tags */}
              {allTags.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-text-muted text-xs uppercase tracking-wide">Tags:</span>
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => setActiveTag(activeTag === tag ? '' : tag)}
                      className={`text-xs px-2.5 py-1 rounded-[2px] border transition-all duration-150 ${
                        activeTag === tag
                          ? 'bg-brand-red border-brand-red text-white' :'bg-transparent border-stroke-primary text-text-muted hover:border-stroke-light hover:text-text-secondary'
                      }`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* AdSense */}
            <AdSenseUnit adSlot="BLOG111111" adFormat="horizontal" className="mb-6" label="Advertisement" />

            {/* Results info + clear */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-text-muted text-xs">
                {hasActiveFilters
                  ? gridPosts.length === 0
                    ? 'No results found'
                    : `${gridPosts.length} result${gridPosts.length !== 1 ? 's' : ''}${searchQuery ? ` for "${searchQuery}"` : ''}`
                  : `${gridPosts.length} article${gridPosts.length !== 1 ? 's' : ''}`}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-brand-red text-xs font-bold uppercase tracking-wide hover:underline flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear filters
                </button>
              )}
            </div>

            {/* Posts Grid */}
            {paginatedPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedPosts.map((post, idx) => (
                  <Link
                    key={post.id}
                    href={`/news/${post.slug}`}
                    className={`group border border-stroke-primary hover:border-brand-red transition-colors duration-200 flex flex-col bg-surface-secondary ${
                      idx === 0 && currentPage === 1 && !hasActiveFilters ? 'md:col-span-2 md:flex-row' : ''
                    }`}
                  >
                    <div
                      className={`relative overflow-hidden bg-surface-tertiary flex-shrink-0 ${
                        idx === 0 && currentPage === 1 && !hasActiveFilters ? 'md:w-1/2' : ''
                      }`}
                      style={{ aspectRatio: idx === 0 && currentPage === 1 && !hasActiveFilters ? undefined : '16/9', minHeight: idx === 0 && currentPage === 1 && !hasActiveFilters ? '220px' : undefined }}
                    >
                      {post.cover_image ? (
                        <img
                          src={post.cover_image}
                          alt={post.cover_image_alt || post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 absolute inset-0"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-10 h-10 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <span className={`text-white text-xs font-bold uppercase px-2 py-0.5 ${CATEGORY_COLORS[post.category || ''] || 'bg-brand-red'}`}>
                          {post.category || 'Blog'}
                        </span>
                      </div>
                    </div>

                    <div className="p-4 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-2 text-xs text-text-muted">
                        <span>{formatDate(post.created_at)}</span>
                        <span>·</span>
                        <span>{estimateReadTime(post.content, post.excerpt)}</span>
                        {post.views !== undefined && post.views > 0 && (
                          <>
                            <span>·</span>
                            <span>{post.views >= 1000 ? `${(post.views / 1000).toFixed(1)}k` : post.views} views</span>
                          </>
                        )}
                      </div>

                      <h3 className="text-text-primary font-bold text-base leading-snug mb-2 group-hover:text-brand-red transition-colors duration-150 line-clamp-2">
                        {post.title}
                      </h3>

                      {post.excerpt && (
                        <p className="text-text-secondary text-sm leading-relaxed line-clamp-3 flex-1 mb-3">
                          {post.excerpt}
                        </p>
                      )}

                      {Array.isArray(post.tags) && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {post.tags.slice(0, 3).map(tag => (
                            <span
                              key={tag}
                              onClick={e => { e.preventDefault(); setActiveTag(activeTag === tag ? '' : tag); }}
                              className={`text-xs px-1.5 py-0.5 rounded-[2px] border cursor-pointer transition-colors duration-150 ${
                                activeTag === tag
                                  ? 'bg-brand-red border-brand-red text-white' :'border-stroke-primary text-text-muted hover:border-stroke-light hover:text-text-secondary'
                              }`}
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-stroke-primary">
                        <span className="text-text-muted text-xs">By {post.author || 'ViddStar'}</span>
                        <span className="text-brand-red text-xs font-bold uppercase tracking-wide group-hover:gap-2 flex items-center gap-1 transition-all duration-150">
                          Read
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 border border-stroke-primary bg-surface-secondary">
                <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <p className="text-text-secondary text-lg mb-1">No articles found</p>
                <p className="text-text-muted text-sm mb-4">Try adjusting your search or filters.</p>
                <button
                  onClick={clearFilters}
                  className="text-brand-red text-sm font-bold uppercase tracking-wide hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-10 space-y-3">
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-stroke-primary text-text-secondary text-xs font-bold uppercase tracking-wide rounded-[4px] hover:border-stroke-light hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                    aria-label="Previous page"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    const isNear = Math.abs(page - currentPage) <= 1 || page === 1 || page === totalPages;
                    if (!isNear) {
                      if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="px-1 text-text-muted text-xs">…</span>;
                      }
                      return null;
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 text-xs font-bold rounded-[4px] border transition-all duration-150 ${
                          currentPage === page
                            ? 'bg-brand-red border-brand-red text-white' :'bg-transparent border-stroke-primary text-text-secondary hover:border-stroke-light hover:text-text-primary'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 border border-stroke-primary text-text-secondary text-xs font-bold uppercase tracking-wide rounded-[4px] hover:border-stroke-light hover:text-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                    aria-label="Next page"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <p className="text-center text-text-muted text-xs">
                  Page {currentPage} of {totalPages} · {gridPosts.length} article{gridPosts.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* AdSense bottom */}
            <AdSenseUnit adSlot="BLOG222222" adFormat="auto" className="mt-10" label="Advertisement" />
          </>
        )}
      </main>

      <footer className="border-t border-stroke-primary bg-surface-secondary mt-12">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-text-muted text-xs">&copy; 2026 ViddStar. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/news" className="text-text-muted hover:text-brand-red text-xs transition-colors">News</Link>
            <Link href="/" className="text-text-muted hover:text-brand-red text-xs transition-colors">← ViddStarTV</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
